import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, Topic, Question, ImportReport, QuestionMedia as MediaRef } from '../api/rest';
import { uploadMedia, MEDIA_ACCEPT } from '../lib/media';
import { currentIdToken } from '../auth/cognito';
import { relativeTime } from '../lib/time';
import { useT } from '../i18n';
import type { GameKind, SideMedia, VoteChoice } from '../shared/protocol';
import { KindToggle, kindOf } from '../components/KindToggle';


const EMPTY_SIDES: SideMedia = { norm: { url: '', type: '' }, cringe: { url: '', type: '' } };
const sidesOrEmpty = (s?: SideMedia): SideMedia => s ?? EMPTY_SIDES;
// Drop the field entirely when neither side has anything, so the API stores nothing.
const sidesOrUndefined = (s: SideMedia): SideMedia | undefined => (s.norm.url || s.cringe.url ? s : undefined);
const toRef = (a: { url: string; type: string }): MediaRef => (a.url ? { mediaUrl: a.url, mediaType: a.type as MediaRef['mediaType'] } : {});
const fromRef = (m: MediaRef): SideMedia['norm'] => ({ url: m.mediaUrl ?? '', type: m.mediaType ?? '' });

export function Authoring() {
  const t = useT();
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const [newTopic, setNewTopic] = useState('');
  const [newKind, setNewKind] = useState<GameKind>('quiz');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'newest' | 'title'>('newest');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) navigate('/admin', { replace: true });
      else void loadTopics();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTopics = async () => {
    try {
      setTopics(await api.listTopics());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  };

  const addTopic = async () => {
    if (!newTopic.trim()) return;
    try {
      await api.createTopic(newTopic.trim(), topics.length, newKind);
      setNewTopic('');
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add topic');
    }
  };

  const removeTopic = async (id: string) => {
    if (!confirm(t('authoring.deleteTopicConfirm'))) return;
    await api.deleteTopic(id);
    await loadTopics();
  };

  const runImport = async (file: File) => {
    setError(null);
    setImportReport(null);
    try {
      const payload = JSON.parse(await file.text());
      const report = await api.importContent(payload);
      setImportReport(report);
      await loadTopics();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('authoring.importFailed'));
    }
  };

  const q = search.trim().toLowerCase();
  const visibleTopics = topics
    .filter((t) => t.title.toLowerCase().includes(q))
    .sort((a, b) => {
      if (sort === 'title') return a.title.localeCompare(b.title);
      // newest first by createdAt (missing dates sort last)
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    });

  return (
    <div className="stack">
      <div className="rowbetween">
        <h1>{t('authoring.title')}</h1>
        <Link to="/admin/games" className="ghost">{t('common.back')}</Link>
      </div>
      {error && <div className="banner error">{t(error)}</div>}

      <div className="card">
        <h2>{t('authoring.bulkImport')}</h2>
        <p className="muted">{t('authoring.importHelp')}</p>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void runImport(f);
            if (fileRef.current) fileRef.current.value = '';
          }}
        />
        {importReport && (
          <div className="banner success">
            {t('authoring.imported', { imported: importReport.imported, skipped: importReport.skipped })}
            <ul>
              {importReport.reports.map((r, i) => (
                <li key={i}>{r.imported ? '✅' : '⚠️'} {r.title} {r.error ? `— ${r.error}` : ''}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card">
        <div className="section-head">
          <h2>{t('authoring.topics')} <span className="chip">{topics.length}</span></h2>
        </div>

        <div className="addrow">
          <input
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTopic()}
            placeholder={t('authoring.newTopicPlaceholder')}
          />
          <KindToggle value={newKind} onChange={setNewKind} />
          <button className="primary" onClick={addTopic} disabled={!newTopic.trim()}>{t('authoring.add')}</button>
        </div>

        <div className="toolbar">
          <div className="searchbox">
            <span className="searchicon">🔍</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('authoring.filterTopics')} />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value as 'newest' | 'title')}>
            <option value="newest">{t('authoring.newestFirst')}</option>
            <option value="title">{t('authoring.titleAZ')}</option>
          </select>
        </div>

        {visibleTopics.length === 0 ? (
          <p className="muted empty">{topics.length === 0 ? t('authoring.noTopics') : t('authoring.noMatch')}</p>
        ) : (
          <ul className="topiclist">
            {visibleTopics.map((topic) => (
              <TopicEditor key={topic.id} topic={topic} onDelete={() => removeTopic(topic.id)} onError={setError} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TopicEditor({ topic, onDelete, onError }: { topic: Topic; onDelete: () => void; onError: (m: string) => void }) {
  const t = useT();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [points, setPoints] = useState(10);
  const [answer, setAnswer] = useState('');
  const [media, setMedia] = useState<MediaRef>({});
  const [correct, setCorrect] = useState<VoteChoice | ''>('');
  const [sides, setSides] = useState<SideMedia>(EMPTY_SIDES);
  // Vote statements are unscored; the answer field doubles as a host-only note.
  const vote = kindOf(topic.kind) === 'normcringe';

  const load = async () => {
    try {
      setQuestions(await api.listQuestions(topic.id));
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const addQuestion = async () => {
    if (!text.trim() || (!vote && points <= 0)) return;
    try {
      await api.createQuestion(topic.id, {
        text: text.trim(), points: vote ? 0 : points, answer: answer.trim(), correct, sides: sidesOrUndefined(sides), ...media,
      });
      setText('');
      setAnswer('');
      setPoints(10);
      setMedia({});
      setCorrect('');
      setSides(EMPTY_SIDES);
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  const rel = relativeTime(topic.createdAt);

  return (
    <li className={`topicitem ${open ? 'open' : ''}`}>
      <div className="topichead" onClick={toggle}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        <span className="ttitle">{topic.title}</span>
        {vote && <span className="chip kind">{t('kind.normcringe')}</span>}
        {open && questions.length > 0 && <span className="chip subtle">{t('authoring.qCount', { n: questions.length })}</span>}
        {rel && <span className="muted small when">{rel}</span>}
        <button className="danger small" onClick={(e) => { e.stopPropagation(); onDelete(); }}>{t('common.delete')}</button>
      </div>
      {open && (
        <div className="questions">
          {questions.map((q) => (
            <QuestionRow key={q.id} q={q} vote={vote} reload={load} onError={onError} />
          ))}
          <div className="qadd">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={vote ? t('authoring.statementText') : t('authoring.questionText')} />
            {!vote && <input type="number" value={points} min={1} onChange={(e) => setPoints(Number(e.target.value))} className="pts" />}
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={vote ? t('authoring.hostNote') : t('authoring.answerHostOnly')} />
            <MediaPicker value={media} onChange={setMedia} onError={onError} />
            {vote && <CorrectPicker value={correct} onChange={setCorrect} />}
            {vote && <SidePickers value={sides} onChange={setSides} onError={onError} />}
            <button className="primary small" onClick={addQuestion}>{t('common.add')}</button>
          </div>
        </div>
      )}
    </li>
  );
}

function QuestionRow({ q, vote, reload, onError }: { q: Question; vote: boolean; reload: () => Promise<void>; onError: (m: string) => void }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(q.text);
  const [points, setPoints] = useState(q.points);
  const [answer, setAnswer] = useState(q.answer ?? '');
  const [media, setMedia] = useState<MediaRef>({ mediaUrl: q.mediaUrl, mediaType: q.mediaType });
  const [correct, setCorrect] = useState<VoteChoice | ''>(q.correct ?? '');
  const [sides, setSides] = useState<SideMedia>(sidesOrEmpty(q.sides));

  const startEdit = () => {
    setText(q.text);
    setPoints(q.points);
    setAnswer(q.answer ?? '');
    setMedia({ mediaUrl: q.mediaUrl, mediaType: q.mediaType });
    setCorrect(q.correct ?? '');
    setSides(sidesOrEmpty(q.sides));
    setEditing(true);
  };

  const save = async () => {
    if (!text.trim() || (!vote && points <= 0)) return;
    try {
      await api.updateQuestion(q.id, {
        text: text.trim(), points: vote ? 0 : points, answer: answer.trim(), order: q.order,
        mediaUrl: media.mediaUrl ?? '', mediaType: media.mediaType, correct, sides: sidesOrUndefined(sides),
      });
      setEditing(false);
      await reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  const remove = async () => {
    if (!confirm(t('authoring.deleteQuestionConfirm'))) return;
    try {
      await api.deleteQuestion(q.id);
      await reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.failedLoad'));
    }
  };

  if (editing) {
    return (
      <div className="qedit">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={vote ? t('authoring.statementText') : t('authoring.questionText')} />
        {!vote && <input type="number" min={1} value={points} onChange={(e) => setPoints(Number(e.target.value))} className="pts" />}
        <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={vote ? t('authoring.hostNote') : t('authoring.answerHostOnly')} />
        <MediaPicker value={media} onChange={setMedia} onError={onError} />
        {vote && <CorrectPicker value={correct} onChange={setCorrect} />}
        {vote && <SidePickers value={sides} onChange={setSides} onError={onError} />}
        <button className="primary small" onClick={save}>{t('common.save')}</button>
        <button className="ghost small" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
      </div>
    );
  }

  return (
    <div className="qrow">
      <span>
        {q.order}. {q.text} {!vote && <span className="points">({q.points})</span>}
        {q.mediaType && <span className="chip subtle" title={q.mediaUrl}>{q.mediaType === 'image' ? '🖼' : '🎵'}</span>}
        {vote && q.correct && <span className="chip subtle">✓ {q.correct === 'norm' ? t('play.voteNorm') : t('play.voteCringe')}</span>}
        {vote && q.sides && (q.sides.norm.url || q.sides.cringe.url) && (
          <span className="chip subtle">{[q.sides.norm, q.sides.cringe].map((a) => (a.type === 'image' ? '🖼' : a.type === 'audio' ? '🎵' : '·')).join(' ')}</span>
        )}
        {q.answer && <span className="muted small"> — {q.answer}</span>}
      </span>
      <span className="row">
        <button className="ghost small" onClick={startEdit}>{t('common.edit')}</button>
        <button className="danger small" onClick={remove}>✗</button>
      </span>
    </div>
  );
}

/**
 * Attach / replace / clear a picture or audio clip on a question. The file goes
 * straight to S3 via a presigned URL; only the resulting /media/ path is kept in
 * the form state and saved with the question.
 */
function MediaPicker({ value, onChange, onError }: { value: MediaRef; onChange: (m: MediaRef) => void; onError: (m: string) => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadMedia(f));
    } catch (err) {
      onError(err instanceof Error ? err.message : t('authoring.uploadFailed'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <span className="mediapick">
      <input ref={fileRef} type="file" accept={MEDIA_ACCEPT} hidden onChange={(e) => void pick(e.target.files?.[0])} />
      {value.mediaType ? (
        <>
          <span className="chip subtle" title={value.mediaUrl}>{value.mediaType === 'image' ? '🖼' : '🎵'} {t(`authoring.media.${value.mediaType}`)}</span>
          <button type="button" className="ghost small" disabled={busy} onClick={() => fileRef.current?.click()}>{t('authoring.replaceMedia')}</button>
          <button type="button" className="ghost small" disabled={busy} onClick={() => onChange({})}>✗</button>
        </>
      ) : (
        <button type="button" className="ghost small" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? t('authoring.uploading') : t('authoring.attachMedia')}
        </button>
      )}
    </span>
  );
}

/** Marks which side of a vote statement is the right answer (or none — pure opinion). */
function CorrectPicker({ value, onChange }: { value: VoteChoice | ''; onChange: (v: VoteChoice | '') => void }) {
  const t = useT();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as VoteChoice | '')} title={t('authoring.correctSide')}>
      <option value="">{t('authoring.correctNone')}</option>
      <option value="norm">✓ {t('play.voteNorm')}</option>
      <option value="cringe">✓ {t('play.voteCringe')}</option>
    </select>
  );
}

/** One upload slot per answer side (two pictures to compare, or two clips). */
function SidePickers({ value, onChange, onError }: { value: SideMedia; onChange: (s: SideMedia) => void; onError: (m: string) => void }) {
  const t = useT();
  return (
    <span className="sidepickers">
      <span className="muted small">{t('authoring.sideMedia')}:</span>
      <span className="sidelbl">👍 {t('play.voteNorm')}</span>
      <MediaPicker value={toRef(value.norm)} onChange={(m) => onChange({ ...value, norm: fromRef(m) })} onError={onError} />
      <span className="sidelbl">👎 {t('play.voteCringe')}</span>
      <MediaPicker value={toRef(value.cringe)} onChange={(m) => onChange({ ...value, cringe: fromRef(m) })} onError={onError} />
    </span>
  );
}
